import * as Yup from "yup";
import { APP_CONFIG } from "../../../constants/constants";
import { TLegacyUserGroup } from "../../../api/types/User";
import { phoneRegex } from "../../../utils/validation";

// form field names
export type TFormFieldNames =
  | "account_id"
  | "account_description"
  | "creation_date"
  | "last_login"
  | "password"
  | "timezone"
  | "is_active"
  | "toggleft"
  | "contact_name"
  | "contact_phone"
  | "contact_email"
  | "unlock_account"
  | "drivers_assigned_to_devices"
  | "session_timeout"
  | "allow_auto_add_devices"
  | "maximum_devices"
  | "max_commands_locates"
  | "total"
  | "account_manager"
  | "is_parent"
  | "parent_id"
  | "private_label_name"
  | "geocoder_mode"
  | "border_crossing"
  | "dcs_properties_id"
  | "sms_properties"
  | "web_services"
  | "driver_elogs"
  | "hos_data_on_maps"
  | "27_hour_reboot_option"
  | "speed_behaviour_option"
  | "dtc_popups_option"
  | "maint_labels"
  | "pto_labels"
  | "view_video_option"
  | "sso_to_vlstracks"
  | "left_menu_hamburger"
  | "driver_groups"
  | "snapped_route"
  | "speed_units"
  | "distance_units"
  | "volume_units"
  | "economy_units"
  | "pressure_units"
  | "temperature_units"
  | "latitude_longitude_format"
  | "route_segment_color_rule"
  | "route_line_thickness"
  | "multi_vehicle_map_name"
  | "default_login_userId"
  | "default_overlay"
  | "enablecrashtrack"
  | "usegeocategories"
  | "incidentreviewallowed"
  | "incabcoachingallowed"
  | "serializedziptieallowed"
  | "userloginasallowed"
  ;

// initial values for the form
export const enterpriseDetailsInitialValues = {
  account_id: "",
  account_description: "",
  creation_date: "",
  last_login: "",
  password: "",
  timezone: "",
  is_active: false,
  toggleft: false,
  contact_name: "",
  contact_phone: "",
  contact_email: "",
  unlock_account: false,
  drivers_assigned_to_devices: false,
  session_timeout: 60 * 60 * 0.5, // 30 minutes
  allow_auto_add_devices: false,
  maximum_devices: "0",
  max_commands_locates: "0",
  total: "0",
  account_manager: "",
  is_parent: false,
  child_accounts: [] as { label: string; value: string }[],
  parent_id: "",
  private_label_name: "",
  geocoder_mode: "",
  border_crossing: false,
  dcs_properties_id: "",
  sms_properties: "",
  web_services: false,
  driver_elogs: false,
  hos_data_on_maps: false,
  "27_hour_reboot_option": false,
  speed_behaviour_option: false,
  dtc_popups_option: false,
  maint_labels: false,
  pto_labels: false,
  view_video_option: false,
  sso_to_vlstracks: false,
  left_menu_hamburger: false,
  driver_groups: false,
  snapped_route: false,
  fleet_tag: false,
  panic_sos: false,
  fuel_card_integration: false,
  snow_plows: false,
  enablecrashtrack: false,
  usegeocategories: false,
  recorder_type: "0",
  recorder_config_allowed: "0",
  speed_units: "0",
  distance_units: "0",
  volume_units: "0",
  economy_units: "0",
  pressure_units: "0",
  temperature_units: "0",
  minidleseconds: 0,
  cameraliveviewduration: 30,
  latitude_longitude_format: "0",
  route_segment_color_rule: "",
  route_line_thickness: "",
  multi_vehicle_map_name: "",
  default_login_userId: "",
  default_overlay: "",
  assetimageonmap: false,
  incidentreviewallowed: false,
  incabcoachingallowed: false,
  serializedziptieallowed: false,
  userloginasallowed: false,
};

// validation schema for the form
export const enterpriseDetailsYupValidationSchema = Yup.object().shape({
  account_id: Yup.string().required("User ID is required"),
  account_description: Yup.string().required("Account Description is required"),
  password: Yup.string().min(
    APP_CONFIG.PASSWORD.MIN_LENGTH,
    `Password is too short - should be minimum ${APP_CONFIG.PASSWORD.MIN_LENGTH} chars`
  ),
  timezone: Yup.string().required("Timezone is required"),
  is_active: Yup.boolean().required("Is Active is required"),
  contact_name: Yup.string().required("Contact Name is required"),
  contact_phone: Yup.string()
    .matches(phoneRegex, "Invalid phone")
    .required("Contact Phone is required"),
  contact_email: Yup.string()
    .required("Email is required")
    .matches(
      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
      "Please enter a valid email address"
    ),
  drivers_assigned_to_devices: Yup.boolean().required(
    "Drivers Assigned to Devices is required"
  ),
  // session timeout to be less than or equal to 64855 (45 days * 24 hours * 60 minites + 55 minutes)
  session_timeout: Yup.number()
    .max(
      64855 * 60, // convert to seconds for validation
      "Session Timeout must be less than or equal to 45 days 55 minutes (maximum value 1080:55)" // 64855 minutes
    )
    .required("Session Timeout is required"),
  // maximum_devices: Yup.number().required("Maximum Devices is required"), //FIELD REMOVED
  // max_commands_locates: Yup.number().required("Max Commands Locates is required"),  //FIELD REMOVED
  // total: Yup.number().required("Total is required"),  //FIELD REMOVED
  //parent_id: Yup.string().required("Parent ID is required"), //NOT MANDATORY
  //private_label_name: Yup.string().required("Private Label Name is required"), //NOT MANDATORY
  // geocoder_mode: Yup.string().required("Geocoder Mode is required"), //FIELD REMOVED
  // dcs_properties_id: Yup.string().required("DCS Properties ID is required"), //FIELD REMOVED
  // sms_properties: Yup.string().required("SMS Properties is required"), //FIELD REMOVED
  recorder_type: Yup.string().when(
    "view_video_option",
    ([view_video_option], schema) => {
      if (view_video_option) {
        return schema
          .test(
            "is-greater-than-zero",
            "Recorder Type must be selected",
            (value) => value !== "0"
          )
          .required(
            "Recorder Type is required when View Video Option is enabled"
          );
      } else {
        return schema.optional(); // required("Default Recorder Type is required");
      }
    }
  ),
  // recorder_type: Yup.string().required("Default Recorder is required"),
  speed_units: Yup.string().required("Speed Units is required"),
  distance_units: Yup.string().required("Distance Units is required"),
  volume_units: Yup.string().required("Volume Units is required"),
  economy_units: Yup.string().required("Economy Units is required"),
  pressure_units: Yup.string().required("Pressure Units is required"),
  temperature_units: Yup.string().required("Temperature Units is required"),
  latitude_longitude_format: Yup.string().required(
    "Latitude Longitude Format is required"
  ),
  minidleseconds: Yup.number().typeError("Minimum Idle Minutes must be a number").required("Minimum Idle Minutes is required")
    .min(0, "Value must be at least 0").max(60, "Value must be at most 60"),
  cameraliveviewduration: Yup.number().typeError("Camera Live View Duration must be a number").required("Camera Live View Duration is required")
    .min(10, "Value must be at least 10 seconds").max(600, "Value must be at most 10 minutes"),
});
